import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, formSetValue, useCpFormAnyValueId, useCpFormModifiedAnyId, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf, request } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Group } from "../../schema/types/group/entity";
import { store } from "../../store";
import { GroupInput } from "../../schema/types/group/input";
import { cloneDeep } from "lodash";
import { GroupDetailHeader } from "./header";
import { Summary } from "./summary";
import { UserItems } from "./userItems";
import { ServiceAccountItems } from "./serviceaccountItems";
import { UserQuery } from "./userQuery";
import { IdentityMatcher } from "./identityMatcher";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  group: Group;
  refetch?: () => void;
}
export const GroupDetailContent: React.FC<Props> = ({ group, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/group/:group/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(group).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(group.lastModified);
  }, [group.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);
  const formVersion = useCpFormValue(`version`);

  React.useEffect(() => {
    if (!formVersion) return;
    fetchUsers();
    fetchServiceAccounts();
  }, [formVersion]);

  async function fetchUsers() {
    const form = store.getState().form.instances[formId];
    const values: GroupInput = form.values;

    const users = cloneDeep(values.users);

    for (const target of users) {
      const link = target.link;
      const { data } = await request({ url: link });
      target.item = data;
    }
    formSetValue(formId, `users`, users);
  }

  async function fetchServiceAccounts() {
    const form = store.getState().form.instances[formId];
    const values: GroupInput = form.values;

    const serviceAccounts = cloneDeep(values.serviceAccounts);

    for (const target of serviceAccounts) {
      const link = target.link;
      const { data } = await request({ url: link });
      target.item = data;
    }
    formSetValue(formId, `serviceAccounts`, serviceAccounts);
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Users", "/users", { names: ["users"], showDirty: true }),
      toLinkPage("Service Accounts", "/serviceaccounts", { names: ["serviceAccounts"], showDirty: true }),
      toLinkPage("User Query", "/user-query", { names: ["userQuery"], showDirty: true }),
      toLinkPage("Identity Matcher", "/identity-matcher", { names: ["identityMatcher"], showDirty: true }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <GroupDetailHeader group={group} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary group={group} />} />
              <Route path={`users/*`} element={<UserItems />} />
              <Route path={`serviceaccounts/*`} element={<ServiceAccountItems />} />
              <Route path={`user-query/*`} element={<UserQuery />} />
              <Route path={`identity-matcher/*`} element={<IdentityMatcher />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"group"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail kind={"group"} itemName={group.name} resourceId={group.id} version={group.version} selfLink={linksOf(group).self!} refetch={refetch} />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(group).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
